/** Operators on a campaign: who they are, whether they can take a call,
 *  and what share of the work they hold — in ONE card.
 *
 *  Reported 2026-09-28: the rail carried two cards listing the same two
 *  people one above the other — "Total operators" with their campaign
 *  load, "Operator availability" with their shift state. Beyond the
 *  repetition, the split hid the thing that mattered: the operator
 *  holding 2% of the load was the same one with no schedule set, and
 *  learning that meant reading one box against the other.
 *
 *  Two sources, deliberately kept separate underneath: campaign load
 *  comes from the campaign's own operator-stats endpoint, presence from
 *  the org-wide availability poll (refetched on its own cadence, and
 *  gated on `org.member.read` — an operator without that permission
 *  still sees the load half rather than nothing).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { PermissionGate } from "@/components/PermissionGate";
import { ApiPaths, api } from "@/lib/api";
import { useHasPermission } from "@/lib/permissions";

import { listAssignees, setAssigneeBookable } from "./assigneesApi";
import { operatorFigures, operatorInitials } from "./operatorFigures";
import { RemoveOperatorModal } from "./RemoveOperatorModal";

type PresenceStatus = "in_call" | "available" | "off_shift" | "no_schedule";

type Presence = {
  user_id: number;
  status: PresenceStatus;
};

type PresenceResponse = {
  operators: Presence[];
  summary: Record<PresenceStatus, number>;
};

type Stat = {
  user_id: number;
  display_name: string;
  email: string;
  booked: number;
  upcoming: number;
  done?: number;
  live?: number;
  missed?: number;
  awaiting?: number;
};

/** Only two states are tinted, and both earn it.
 *
 *  `in_call` because something is happening right now, and `no_schedule`
 *  because it is a configuration gap that silently inflates the
 *  campaign's capacity — `daily_slot_capacity` counts heads, and the
 *  booking engine treats "no schedule" as always-available, so that
 *  operator's slots really are offered at any hour.
 *
 *  `available` and `off_shift` stay neutral. They are just the day, and
 *  a card that tints every state tints nothing. */
const SKIN: Record<PresenceStatus | "unknown", string> = {
  in_call:
    "border-nb-mint/35 bg-gradient-to-br from-nb-mint/10 to-nb-bg2",
  no_schedule:
    "border-nb-warn/35 bg-gradient-to-br from-nb-warn/10 to-nb-bg2",
  available: "border-nb-border bg-gradient-to-br from-sky-500/[0.08] to-nb-bg2",
  off_shift: "border-nb-border bg-gradient-to-br from-sky-500/[0.08] to-nb-bg2",
  unknown: "border-nb-border bg-gradient-to-br from-sky-500/[0.08] to-nb-bg2",
};

const EDGE: Record<PresenceStatus | "unknown", string> = {
  in_call: "bg-nb-mint",
  no_schedule: "bg-nb-warn",
  available: "bg-sky-500",
  off_shift: "bg-sky-500",
  unknown: "bg-sky-500",
};

const PILL: Record<PresenceStatus | "unknown", string> = {
  in_call: "border-nb-mint/50 bg-nb-mint/15 text-nb-mint",
  no_schedule: "border-nb-warn/45 bg-nb-warn/12 text-amber-300",
  available: "border-nb-mint/35 text-nb-mint",
  off_shift: "border-nb-border text-nb-text-3",
  unknown: "border-nb-border text-nb-text-3",
};

const DOT: Record<PresenceStatus | "unknown", string> = {
  in_call: "bg-nb-mint",
  available: "bg-nb-mint",
  off_shift: "bg-nb-text-3",
  no_schedule: "bg-nb-warn",
  unknown: "bg-nb-text-3",
};

const LABEL: Record<PresenceStatus, string> = {
  in_call: "admin.operators.presence.inCall",
  available: "admin.operators.presence.available",
  off_shift: "admin.operators.presence.offShift",
  no_schedule: "admin.operators.presence.noSchedule",
};

export function CampaignOperatorPanel({
  campaignId,
  capacityPerDay,
  bookedAhead,
}: {
  campaignId: string;
  capacityPerDay?: number;
  bookedAhead?: number;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canSeePresence = useHasPermission("org.member.read");
  const [removing, setRemoving] = useState<{ id: number; name: string } | null>(
    null,
  );

  const pause = useMutation({
    mutationFn: ({ userId, next }: { userId: number; next: boolean }) =>
      setAssigneeBookable(campaignId, userId, next),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["campaign-assignees", campaignId] });
      void qc.invalidateQueries({ queryKey: ["campaign-breakdown", campaignId] });
    },
  });

  const assignees = useQuery({
    queryKey: ["campaign-assignees", campaignId],
    queryFn: () => listAssignees(campaignId),
    retry: false,
  });

  const stats = useQuery({
    queryKey: ["campaign-operator-stats", campaignId],
    queryFn: async () =>
      (
        await api.get<{ operator_count: number; operators: Stat[] }>(
          ApiPaths.campaignOperatorStats(campaignId),
        )
      ).data,
    retry: false,
  });

  const presence = useQuery({
    queryKey: ["operator-availability"],
    queryFn: async () =>
      (await api.get<PresenceResponse>(ApiPaths.operatorAvailability)).data,
    refetchInterval: 20000,
    enabled: canSeePresence,
    retry: false,
  });

  const ops = stats.data?.operators ?? [];
  if (ops.length === 0) return null;

  const statusOf = new Map(
    (presence.data?.operators ?? []).map((p) => [p.user_id, p.status]),
  );
  const totalBooked = ops.reduce((s, o) => s + o.booked, 0);
  const summary = presence.data?.summary;
  const bookableOf = new Map(
    (assignees.data ?? []).map((a) => [a.user_id, a.is_bookable !== false]),
  );
  const idle =
    capacityPerDay !== undefined
      ? Math.max(0, capacityPerDay - (bookedAhead ?? 0))
      : null;

  return (
    <section className="card space-y-2.5">
      <h2 className="flex items-baseline text-sm font-semibold uppercase tracking-wide text-nb-text-2">
        {t("campaigns.detail.operatorsPanelTitle")}
        <span className="ml-auto font-mono text-xs font-normal normal-case tracking-normal text-nb-text-3">
          {ops.length}
        </span>
      </h2>

      <ul className="flex flex-col gap-2">
        {ops.map((o) => {
          const status = statusOf.get(o.user_id) ?? "unknown";
          const { done, upcoming, awaiting, sharePct } = operatorFigures(
            o,
            totalBooked,
          );
          return (
            <li
              key={o.user_id}
              className={`relative overflow-hidden rounded-lg border p-2.5 pl-3 ${SKIN[status]}`}
            >
              <span
                aria-hidden="true"
                className={`absolute inset-y-0 left-0 w-[2px] ${EDGE[status]}`}
              />

              <div className="flex items-center gap-2">
                <span
                  aria-hidden="true"
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-nb-surface-2 text-[10px] font-semibold uppercase text-nb-text-2 ring-1 ring-nb-border"
                >
                  {operatorInitials(o.display_name)}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-semibold">
                  {o.display_name}
                </span>
                {status !== "unknown" && (
                  <span
                    className={`flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-1 font-mono text-[9px] uppercase tracking-wider ${PILL[status]}`}
                  >
                    <span
                      aria-hidden="true"
                      className={`h-1.5 w-1.5 rounded-full ${DOT[status]}`}
                    />
                    {t(LABEL[status])}
                  </span>
                )}
              </div>

              <div className="mt-2 grid grid-cols-3 gap-1 text-center">
                {/* Done / Upcoming / Awaiting, not Booked / Upcoming /
                    Done. "Booked" counted every invitation as an
                    appointment — 520 of them on a campaign with 59
                    actual slots — and "Done" swept up everyone who had
                    simply not replied yet. */}
                <Fig
                  label={t("campaigns.detail.opDone")}
                  value={done}
                  tone="text-emerald-400"
                />
                <Fig
                  label={t("campaigns.detail.colUpcoming")}
                  value={upcoming}
                  tone="text-sky-400"
                />
                <Fig label={t("campaigns.detail.opAwaiting")} value={awaiting} />
              </div>

              <div className="mt-2 flex flex-col gap-1">
                <span className="flex items-baseline justify-between text-[11px] text-nb-text-3">
                  <span>{t("campaigns.detail.shareOfCampaign")}</span>
                  <span className="font-mono font-semibold text-nb-text-2">
                    {sharePct}%
                  </span>
                </span>
                <span className="h-1.5 overflow-hidden rounded-full bg-nb-bg/70">
                  <span
                    className={`block h-full rounded-full ${
                      status === "no_schedule" ? "bg-nb-warn" : "bg-sky-500"
                    }`}
                    style={{ width: `${Math.max(1, sharePct)}%` }}
                  />
                </span>
              </div>

              <PermissionGate perm="campaign.members.manage">
                <div className="mt-2 flex items-center gap-2 border-t border-nb-border/70 pt-2">
                  <button
                    type="button"
                    className="text-[11px] font-medium text-nb-text-3 hover:text-nb-text"
                    disabled={pause.isPending}
                    onClick={() =>
                      pause.mutate({
                        userId: o.user_id,
                        next: !(bookableOf.get(o.user_id) ?? true),
                      })
                    }
                  >
                    {(bookableOf.get(o.user_id) ?? true)
                      ? t("campaigns.detail.pauseOperator")
                      : t("campaigns.detail.resumeOperator")}
                  </button>
                  <button
                    type="button"
                    className="ml-auto text-[11px] font-medium text-nb-text-3 hover:text-cvp-danger"
                    onClick={() =>
                      setRemoving({ id: o.user_id, name: o.display_name })
                    }
                  >
                    {t("common.remove")}
                  </button>
                </div>
              </PermissionGate>

              {/* Paused is a state worth naming: they keep the campaign
                  and their past work, they just stop being booked. */}
              {bookableOf.get(o.user_id) === false && (
                <p className="mt-1.5 flex items-start gap-1 text-[11px] leading-snug text-nb-text-3">
                  <span aria-hidden>⏸</span>
                  {t("campaigns.detail.pausedNote")}
                </p>
              )}

              {/* The fact the two old cards never connected. */}
              {status === "no_schedule" && bookableOf.get(o.user_id) !== false && (
                <p className="mt-2 flex items-start gap-1 text-[11px] leading-snug text-amber-300">
                  <span aria-hidden>⚠</span>
                  {t("campaigns.detail.noScheduleWarning")}
                </p>
              )}
            </li>
          );
        })}
      </ul>

      {removing && (
        <RemoveOperatorModal
          campaignId={campaignId}
          userId={removing.id}
          displayName={removing.name}
          candidates={ops
            .filter(
              (o) =>
                o.user_id !== removing.id &&
                (bookableOf.get(o.user_id) ?? true),
            )
            .map((o) => ({ user_id: o.user_id, display_name: o.display_name }))}
          onClose={() => setRemoving(null)}
        />
      )}

      <div className="flex flex-col gap-0.5 border-t border-nb-border pt-2 text-[11px] text-nb-text-3">
        {summary && (
          <span>
            {t("campaigns.detail.presenceSummary", {
              inCall: summary.in_call,
              available: summary.available,
            })}
          </span>
        )}
        {idle !== null && capacityPerDay !== undefined && (
          <span>
            {t("campaigns.detail.idleSlots", {
              idle: Math.round(idle),
              capacity: capacityPerDay,
            })}
          </span>
        )}
      </div>
    </section>
  );
}

function Fig({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: string;
}) {
  return (
    <div className="rounded-md border border-nb-border/70 bg-nb-bg/55 px-1 py-1">
      <div className={`font-mono text-[15px] font-bold tabular-nums ${tone ?? ""}`}>
        {value}
      </div>
      <div className="mt-0.5 text-[9px] uppercase tracking-wide text-nb-text-3">
        {label}
      </div>
    </div>
  );
}
